import React, { useEffect, useState } from "react";
import { Row, Button, Form, Input, Modal } from "antd";
import { useNavigate } from "react-router";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { useAuthState } from "../provider/AuthProvider";
import { AlertType, useAlert } from "../provider/AlertProvider";
import { CameraFilled } from "@ant-design/icons";

import * as Common from "../commons/common";
import * as Request from "../commons/request";
import * as Utils from "../utils/utils";
import * as String from "../commons/string";

import styles from "./Login.module.css";
import PageContainer from "../layout/PageContainer";

declare const window: typeof globalThis & {
    Android: any;
    webkit: any;
};

type TypeRegisterCar = {
    car: {
        carNumber: string;
    };
    user: {
        email: string;
    };
    active: boolean;
};

function RegisterCar() {
    const navigate = useNavigate();
    const userDetails = useAuthState();
    const alert = useAlert();

    const [form] = Form.useForm();
    const [carNum, setCarNum] = useState("");
    const [serialNum, setSerialNum] = useState("");

    useEffect(() => {
        window.addEventListener("resultQRCodeScanning", onResultQRCodeScanning);
        Utils.onEvent(Common.EVENT_HEADER_INFO, handleInfoPress);
        Utils.onEvent(Common.EVENT_CLOSE_ALL_MODAL, closeAllModal);

        return () => {
            window.removeEventListener("resultQRCodeScanning", onResultQRCodeScanning);
            Utils.offEvent(Common.EVENT_HEADER_INFO, handleInfoPress);
            Utils.offEvent(Common.EVENT_CLOSE_ALL_MODAL, closeAllModal);
        };
    }, []);

    const handleInfoPress = () => {
        navigate(Common.PAGE_REGISTER_CAR_METHOD);
    };

    const onResultQRCodeScanning = async (event: any) => {
        form.setFieldValue("carNumber", event.detail.carNumber);
        form.setFieldValue("serialNumber", event.detail.serialNumber);

        setCarNum(event.detail.carNumber);
        setSerialNum(event.detail.serialNumber);
    };

    const closeAllModal = () => {
        setOpenModalQRError(false);
        setOpenModalQRInfo(false);
    };

    const handleQRClick = () => {
        if (window.Android) window.Android.scanQRCode();
        else if (window.webkit) {
            window.webkit.messageHandlers.scanQRCode.postMessage("scanQRCode");
        } else {
            setOpenModalQRError(true);
        }
    };

    const requestAxiosRegisterCar = async (carInfo: TypeRegisterCar) => {
        const response = await utilAxiosWithAuth().post(Request.CAR_OWNERS, carInfo);
        return response.data;
    };

    const {
        loading: loadingRegisterCar,
        error: errorRegisterCar,
        data: resultRegisterCar,
        execute: requestRegisterCar,
    } = useAsyncAxios(requestAxiosRegisterCar);

    useEffect(() => {
        if (!resultRegisterCar) return;
        console.log("resultRegisterCar", resultRegisterCar);

        alert.setAlert(AlertType.SUCCESS, "성공", "차량이 등록되었습니다.");
        navigate(-1);
    }, [resultRegisterCar]);

    useEffect(() => {
        if (!errorRegisterCar) return;

        console.log("errorRegisterCar", errorRegisterCar);
    }, [errorRegisterCar]);

    const onRegisterCar = (values: any) => {
        const carInfo = {
            car: {
                carNumber: values.carNumber,
            },
            user: {
                email: userDetails?.user?.email,
            },
            active: true,
        };

        requestRegisterCar(carInfo);
    };

    const [openModalQRError, setOpenModalQRError] = useState(false);
    useEffect(() => {
        openModalQRError ? Utils.addModalStack() : Utils.popModalStack();
    }, [openModalQRError]);

    const [openModalQRInfo, setOpenModalQRInfo] = useState(true);
    useEffect(() => {
        openModalQRInfo ? Utils.addModalStack() : Utils.popModalStack();
    }, [openModalQRInfo]);

    return (
        <PageContainer pageBackground={"white"} contentPadding={16}>
            <Row align="middle" justify="center">
                <div style={{ textAlign: "center" }} className={`${styles.loginCard} container`}>
                    <Button className={styles.qrScanBtn} onClick={handleQRClick}>
                        <CameraFilled height={18} />
                        <span>QR코드 촬영</span>
                    </Button>

                    <Form form={form} onFinish={onRegisterCar} layout="vertical">
                        <Form.Item
                            className={styles.textInputLabel}
                            name="carNumber"
                            label="차량 번호"
                            rules={[
                                {
                                    required: true,
                                    message: "차량 번호를 입력해 주세요.",
                                },
                            ]}
                        >
                            <Input
                                className={styles.loginInput}
                                disabled
                                placeholder="차량 번호"
                                value={carNum}
                                onChange={(e) => setCarNum(e.target.value)}
                            />
                        </Form.Item>
                        <Form.Item
                            className={styles.textInputLabel}
                            name="serialNumber"
                            label="시리얼 넘버"
                            rules={[
                                {
                                    required: true,
                                    message: "시리얼 넘버를 입력해 주세요.",
                                },
                            ]}
                        >
                            <Input
                                className={styles.loginInput}
                                placeholder="시리얼 넘버"
                                value={serialNum}
                                disabled
                                onChange={(e) => setSerialNum(e.target.value)}
                            />
                        </Form.Item>
                        <Form.Item className={styles.loginBtnWrapper}>
                            <Button
                                className={styles.loginBtn}
                                block
                                type="primary"
                                htmlType="submit"
                                size="large"
                                disabled={carNum && serialNum ? false : true}
                            >
                                등록하기
                            </Button>
                        </Form.Item>
                    </Form>

                    <Modal
                        key="modalQRInfo"
                        open={openModalQRInfo}
                        closable={false}
                        onCancel={() => setOpenModalQRInfo(false)}
                        footer={null}
                        mask={false}
                        className={styles.qrScanTooltip}
                    >
                        카메라로 차량 단말기 QR코드를 스캔하면 차량 번호와 시리얼 넘버가 등록됩니다.
                        <div
                            style={{
                                marginTop: 8,
                                fontSize: 12,
                                color: "#061A42",
                                textDecoration: "underline",
                                textUnderlineOffset: "2px",
                            }}
                        >
                            <span
                                onClick={() => {
                                    navigate(Common.PAGE_REGISTER_CAR_METHOD);
                                }}
                            >
                                QR코드는 어디에 있나요?
                            </span>
                        </div>
                    </Modal>

                    <Modal
                        className="confirmModal"
                        centered
                        key="modalQRError"
                        open={openModalQRError}
                        closable={false}
                        onCancel={() => setOpenModalQRError(false)}
                        footer={[
                            <Button block type="primary" key="confirm" onClick={() => setOpenModalQRError(false)}>
                                {String.confirm}
                            </Button>,
                        ]}
                    >
                        <Row justify={"center"} align={"middle"}>
                            안드로이드 앱에서만 지원됩니다. 고객센터로 문의 해 주세요. <br />
                            (070-7707-1768)
                        </Row>
                    </Modal>
                </div>
            </Row>
        </PageContainer>
    );
}

export default RegisterCar;
